import React, { useState, useEffect } from "react";
import ConfirmDialog from "../../components/ConfirmationDialog";
import axios from "axios";
import { CiEdit } from "react-icons/ci";
import { FaRegTrashAlt } from "react-icons/fa";
import { IoCall } from "react-icons/io5";
import { FaCheckCircle, FaHistory } from "react-icons/fa";
import { notify } from "../../helpers/utils";
import {
  Plus, Search, Users, Building2, Zap, Calendar,
  Phone, X, ChevronRight, Award, Briefcase
} from "lucide-react";

// ── Design tokens ─────────────────────────────────────────────────
const T = {
  bg:      "var(--nd-bg)",
  surface: "var(--nd-surface)",
  surf2:   "var(--nd-surface-2)",
  surf3:   "var(--nd-surface-2)",
  border:  "var(--nd-w08)",
  bord2:   "var(--nd-w10)",
  text:    "var(--nd-text)",
  muted:   "var(--nd-tx35)",
  faint:   "var(--nd-w10)",
  blue:    "#3B82F6",
  teal:    "#22D3A5",
  amber:   "#FBBF24",
  red:     "#F87171",
  purple:  "#A78BFA",
  green:   "#34D399",
  font:    "'DM Sans', sans-serif",
  mono:    "'DM Mono', monospace",
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes fadeUp  { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
  @keyframes modalIn { from{opacity:0;transform:scale(0.97)} to{opacity:1;transform:scale(1)} }

  .tm-card {
    animation: fadeUp 0.35s ease both;
    transition: transform 0.22s cubic-bezier(0.4,0,0.2,1), box-shadow 0.22s;
  }
  .tm-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 44px rgba(0,0,0,0.45) !important;
  }
  .tm-stat-card { transition: transform 0.2s, box-shadow 0.2s; cursor: default; }
  .tm-stat-card:hover { transform: translateY(-3px); box-shadow: 0 12px 28px rgba(0,0,0,0.35) !important; }

  .tm-filter-btn {
    padding: 8px 18px; border-radius: 9px; font-size: 12px; font-weight: 700;
    cursor: pointer; font-family: 'DM Sans', sans-serif;
    border: 1px solid var(--nd-w08); transition: all 0.15s;
    background: var(--nd-w04); color: var(--nd-tx40);
  }
  .tm-filter-btn:hover { background: var(--nd-w08); color: var(--nd-tx70); }
  .tm-filter-btn.active-all    { background: rgba(59,130,246,0.15);  color: #60A5FA; border-color: rgba(59,130,246,0.35); }
  .tm-filter-btn.active-comm   { background: rgba(167,139,250,0.14); color: #A78BFA; border-color: rgba(167,139,250,0.35); }
  .tm-filter-btn.active-ind    { background: rgba(34,211,165,0.12);  color: #22D3A5; border-color: rgba(34,211,165,0.30); }

  .tm-history-row {
    display: flex; align-items: center; justify-content: space-between;
    padding: 12px 14px; border-radius: 10px;
    background: var(--nd-w03); border: 1px solid var(--nd-w06);
    transition: background 0.15s;
  }
  .tm-history-row:hover { background: var(--nd-w06); }

  .tm-input {
    width: 100%; padding: 10px 12px;
    background: var(--nd-w05); border: 1px solid var(--nd-w10);
    border-radius: 10px; color: var(--nd-text);
    font-size: 13px; font-family: 'DM Sans', sans-serif;
    outline: none; box-sizing: border-box;
    transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
  }
  .tm-input:focus {
    background: var(--nd-w08);
    border-color: rgba(96,165,250,0.5);
    box-shadow: 0 0 0 3px rgba(96,165,250,0.10);
  }
  .tm-input::placeholder { color: var(--nd-w20); }

  .tm-select {
    width: 100%; padding: 10px 12px;
    background: var(--nd-w05); border: 1px solid var(--nd-w10);
    border-radius: 10px; color: var(--nd-text);
    font-size: 13px; font-family: 'DM Sans', sans-serif;
    outline: none; cursor: pointer; box-sizing: border-box;
    -webkit-appearance: none; appearance: none;
    transition: border-color 0.15s;
  }
  .tm-select:focus { border-color: rgba(96,165,250,0.5); box-shadow: 0 0 0 3px rgba(96,165,250,0.10); }
  .tm-select option { background: var(--nd-surface-3); color: var(--nd-text); }

  .tm-select-multi {
    width: 100%; padding: 8px;
    background: var(--nd-w04); border: 1px solid var(--nd-w10);
    border-radius: 10px; color: var(--nd-text);
    font-size: 13px; font-family: 'DM Sans', sans-serif;
    outline: none; min-height: 120px; box-sizing: border-box;
  }
  .tm-select-multi option { padding: 6px 8px; }
  .tm-select-multi option:checked { background: rgba(59,130,246,0.25); color: var(--nd-text-strong); }

  .tm-textarea {
    width: 100%; padding: 10px 12px;
    background: var(--nd-w05); border: 1px solid var(--nd-w10);
    border-radius: 10px; color: var(--nd-text);
    font-size: 13px; font-family: 'DM Sans', sans-serif;
    outline: none; resize: vertical; box-sizing: border-box;
  }
  .tm-textarea:focus { border-color: rgba(96,165,250,0.5); box-shadow: 0 0 0 3px rgba(96,165,250,0.10); }
  .tm-textarea::placeholder { color: var(--nd-w20); }

  .tm-label {
    display: block; font-size: 10px; font-weight: 700;
    letter-spacing: 0.10em; text-transform: uppercase;
    color: var(--nd-tx35); margin-bottom: 6px;
  }

  .tm-btn-primary {
    padding: 10px 20px; border: none; border-radius: 10px; cursor: pointer;
    font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 700;
    background: linear-gradient(135deg,#3B82F6,#22D3A5); color: var(--nd-text-strong);
    display: flex; align-items: center; justify-content: center; gap: 7px;
    box-shadow: 0 4px 14px rgba(59,130,246,0.3);
    transition: opacity 0.15s, transform 0.15s;
  }
  .tm-btn-primary:hover { opacity: 0.9; transform: translateY(-1px); }

  .tm-btn-ghost {
    padding: 10px 20px; border: 1px solid var(--nd-w12); border-radius: 10px; cursor: pointer;
    font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600;
    background: var(--nd-w04); color: var(--nd-tx60);
    display: flex; align-items: center; justify-content: center; gap: 7px;
    transition: background 0.15s;
  }
  .tm-btn-ghost:hover { background: var(--nd-w08); color: var(--nd-text); }

  .tm-action-btn {
    width: 34px; height: 34px; border-radius: 9px; border: none; cursor: pointer;
    display: flex; align-items: center; justify-content: center; transition: all 0.15s;
    flex-shrink: 0;
  }

  .tm-input-icon { position: relative; }
  .tm-input-icon svg { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); pointer-events: none; }
  .tm-input-icon .tm-input { padding-left: 38px; }

  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: var(--nd-w10); border-radius: 4px; }
`;

// ── Type config ───────────────────────────────────────────────────
const TYPE_CFG = {
  commercial: { color: "#A78BFA", bg: "rgba(167,139,250,0.12)", border: "rgba(167,139,250,0.28)", label: "Commercial" },
  industrial: { color: "#22D3A5", bg: "rgba(34,211,165,0.10)",  border: "rgba(34,211,165,0.26)",  label: "Industrial"  },
  residential: { color: "#60A5FA", bg: "rgba(59,130,246,0.12)", border: "rgba(59,130,246,0.28)",  label: "Residential" },
};

// ── Stat Card ─────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, accent, icon: Icon, delay = 0 }) => (
  <div className="tm-stat-card" style={{
    background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14,
    padding: "22px 24px", boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
    animation: `fadeUp 0.4s ${delay}s ease both`, position: "relative", overflow: "hidden",
  }}>
    <div style={{ position: "absolute", top: 0, right: 0, width: 80, height: 80, borderRadius: "0 14px 0 80px", background: `${accent}0D` }} />
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
      <div style={{ width: 28, height: 28, borderRadius: 8, background: `${accent}18`, border: `1px solid ${accent}35`, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon size={13} color={accent} />
      </div>
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", color: T.muted, textTransform: "uppercase" }}>{label}</span>
    </div>
    <div style={{ fontSize: 36, fontWeight: 800, color: accent, letterSpacing: "-1px", fontFamily: T.mono, lineHeight: 1 }}>{value}</div>
    <div style={{ fontSize: 11, color: T.muted, marginTop: 6 }}>{sub}</div>
  </div>
);

// ── Modal overlay ─────────────────────────────────────────────────
const Overlay = ({ onClick, children, zIndex = 9999 }) => (
  <div onClick={onClick} style={{
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)",
    backdropFilter: "blur(5px)", display: "flex", alignItems: "center",
    justifyContent: "center", zIndex, padding: 20,
  }}>{children}</div>
);

// ── Close button ──────────────────────────────────────────────────
const CloseBtn = ({ onClick }) => (
  <button onClick={onClick} style={{
    background: "var(--nd-w06)", border: `1px solid ${T.border}`,
    color: T.muted, width: 34, height: 34, borderRadius: 9, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s",
    flexShrink: 0,
  }}
    onMouseEnter={e => e.currentTarget.style.background = "var(--nd-w12)"}
    onMouseLeave={e => e.currentTarget.style.background = "var(--nd-w06)"}
  ><X size={15} /></button>
);

// ── Main Component ────────────────────────────────────────────────
const TeamManagement = () => {
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedTeamHistory, setSelectedTeamHistory] = useState(null);
  const [jobHistoryData, setJobHistoryData] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");

  const blankForm = {
    team_lead_user_id: "", team_lead_name: "", team_member_user_ids: [],
    team_type: "commercial", max_daily_capacity: 3, phone: "", notes: "",
  };
  const [formData, setFormData] = useState(blankForm);

  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, teamId: null, teamName: "" });

  useEffect(() => { fetchTeams(); fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const r = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/schedule/users/available`);
      setUsers(r.data.users || []);
    } catch (e) { console.error("Error fetching users:", e); }
  };

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/schedule/teams?active_only=true`);
      setTeams(r.data.teams || []);
    } catch (e) {
      console.error("Error fetching teams:", e);
      notify("error", "Failed to fetch teams");
      setTeams([]);
    } finally { setLoading(false); }
  };

  const handleViewHistory = async (team) => {
    setSelectedTeamHistory(team);
    setShowHistoryModal(true);
    setHistoryLoading(true);
    setJobHistoryData([]);
    try {
      const token = localStorage.getItem("token");
      const r = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/schedule/history?team_id=${team.id}&status=completed`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setJobHistoryData(r.data.jobs || []);
    } catch (e) {
      console.error("❌ Error fetching history:", e);
      if (e.response?.status === 401) notify("error", "Session expired. Please login again.");
      else notify("error", "Failed to fetch job history");
    } finally { setHistoryLoading(false); }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "team_lead_user_id") {
      const sel = users.find(u => u.id === parseInt(value));
      if (sel) { setFormData(p => ({ ...p, team_lead_user_id: value, team_lead_name: sel.name, phone: sel.phone || p.phone })); return; }
    }
    setFormData(p => ({ ...p, [name]: value }));
  };

  const handleTeamMemberChange = (e) => {
    const ids = Array.from(e.target.selectedOptions, o => parseInt(o.value));
    setFormData(p => ({ ...p, team_member_user_ids: ids }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.team_lead_user_id) { notify("error", "Please select a team lead"); return; }
    try {
      const payload = {
        team_lead_user_id: parseInt(formData.team_lead_user_id),
        team_lead_name: formData.team_lead_name,
        team_member_user_ids: formData.team_member_user_ids,
        team_type: formData.team_type,
        max_daily_capacity: parseInt(formData.max_daily_capacity),
        phone: formData.phone, notes: formData.notes,
      };
      if (editingTeam) {
        await axios.put(`${process.env.REACT_APP_API_BASE_URL}/schedule/teams/${editingTeam.id}`, payload);
        notify("success", "Team updated successfully!");
      } else {
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/schedule/teams`, payload);
        notify("success", "Team created successfully!");
      }
      setShowModal(false); resetForm(); fetchTeams();
    } catch (e) {
      console.error("Error saving team:", e);
      notify("error", e.response?.data?.message || "Failed to save team");
    }
  };

  const handleEdit = (team) => {
    setEditingTeam(team);
    setFormData({
      team_lead_user_id: team.team_lead_user_id || "",
      team_lead_name: team.team_lead_name,
      team_member_user_ids: team.team_member_user_ids || [],
      team_type: team.team_type,
      max_daily_capacity: team.max_daily_capacity,
      phone: team.phone || "",
      notes: team.notes || "",
    });
    setShowModal(true);
  };

  const handleDelete = (teamId, teamName) => setConfirmDialog({ isOpen: true, teamId, teamName });

  const confirmDelete = async () => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/schedule/teams/${confirmDialog.teamId}`);
      notify("success", "Team deactivated successfully!");
      fetchTeams();
    } catch (e) { console.error("Error deleting team:", e); notify("error", "Failed to deactivate team"); }
  };

  const resetForm = () => { setFormData(blankForm); setEditingTeam(null); };
  const openAddModal = () => { resetForm(); setShowModal(true); };

  const filteredTeams = teams.filter(t => {
    const matchSearch = t.team_lead_name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchSearch && (filterType === "all" || t.team_type === filterType);
  });

  const stats = {
    total: teams.length,
    commercial: teams.filter(t => t.team_type === "commercial").length,
    industrial: teams.filter(t => t.team_type === "industrial").length,
    totalCapacity: teams.reduce((s, t) => s + (t.max_daily_capacity || 0), 0),
  };

  const getUserName = (userId) => {
    const u = users.find(u => u.id === userId);
    return u ? u.name : "Unknown User";
  };

  // ── Full-page loading ──
  if (loading) {
    return (
      <div style={{ background: T.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: T.font }}>
        <style>{STYLES}</style>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 48, height: 48, border: `3px solid ${T.border}`, borderTop: `3px solid ${T.blue}`, borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
          <div style={{ color: T.muted, fontSize: 14 }}>Loading teams…</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: T.bg, fontFamily: T.font, color: T.text, padding: "14px 20px" }}>
      <style>{STYLES}</style>

      {/* ── PAGE HEADER ── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, marginBottom: 20, overflow: "hidden" }}>
        <div style={{ padding: "20px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${T.border}` }}>
          <div>
            <div style={{ fontSize: 9, color: T.muted, letterSpacing: "0.18em", marginBottom: 5 }}>NOVAA · C&I MANAGEMENT</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: "var(--nd-text-strong)", letterSpacing: "-0.03em", lineHeight: 1 }}>Team Management</div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 5 }}>Manage installation teams and assign field operators</div>
          </div>
          <button className="tm-btn-primary" onClick={openAddModal}>
            <Plus size={16} /> Add New Team
          </button>
        </div>
        <div style={{ height: 2, background: "linear-gradient(90deg,#3B82F6,#22D3A5,transparent)" }} />
      </div>

      {/* ── STAT CARDS ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 16, marginBottom: 20 }}>
        <StatCard label="Total Teams"    value={stats.total}         sub="Active teams"    accent={T.blue}   icon={Users}     delay={0}    />
        <StatCard label="Commercial"     value={stats.commercial}    sub="Teams"           accent={T.purple} icon={Building2} delay={0.05} />
        <StatCard label="Industrial"     value={stats.industrial}    sub="Teams"           accent={T.teal}   icon={Zap}       delay={0.10} />
        <StatCard label="Daily Capacity" value={stats.totalCapacity} sub="Sites / day"     accent={T.green}  icon={Calendar}  delay={0.15} />
      </div>

      {/* ── SEARCH + FILTER ── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: "14px 18px", marginBottom: 20, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <div className="tm-input-icon" style={{ flex: 1, minWidth: 220 }}>
          <Search size={15} color="var(--nd-tx25)" />
          <input type="text" className="tm-input" placeholder="Search teams by lead name…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {[
            { id: "all",        label: "All Teams",   cls: "active-all"  },
            { id: "commercial", label: "Commercial",  cls: "active-comm" },
            { id: "industrial", label: "Industrial",  cls: "active-ind"  },
          ].map(f => (
            <button key={f.id} className={`tm-filter-btn ${filterType === f.id ? f.cls : ""}`} onClick={() => setFilterType(f.id)}>{f.label}</button>
          ))}
        </div>
        <div style={{ fontSize: 11, color: T.muted, fontFamily: T.mono, marginLeft: "auto" }}>
          {filteredTeams.length} / {teams.length} teams
        </div>
      </div>

      {/* ── TEAM CARDS GRID ── */}
      {filteredTeams.length > 0 ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(360px,1fr))", gap: 20 }}>
          {filteredTeams.map((team, idx) => {
            const cfg = TYPE_CFG[team.team_type] || TYPE_CFG.residential;
            const initial = team.team_lead_name.charAt(0).toUpperCase();

            return (
              <div key={team.id} className="tm-card" style={{
                background: T.surface, border: `1px solid ${T.border}`, borderRadius: 18,
                padding: 22, boxShadow: "0 4px 20px rgba(0,0,0,0.3)", position: "relative",
                overflow: "hidden", animationDelay: `${idx * 0.07}s`,
              }}>
                {/* Corner accent */}
                <div style={{ position: "absolute", top: 0, right: 0, width: 90, height: 90, borderRadius: "0 18px 0 90px", background: `${cfg.color}0C` }} />

                {/* Card header */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18, position: "relative", zIndex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    {/* Avatar */}
                    <div style={{
                      width: 52, height: 52, borderRadius: 14, flexShrink: 0,
                      background: cfg.bg, border: `1.5px solid ${cfg.border}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 22, fontWeight: 800, color: cfg.color,
                    }}>{initial}</div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: T.text, lineHeight: 1.2, marginBottom: 5 }}>
                        {team.team_lead_name}'s Team
                      </div>
                      <span style={{
                        display: "inline-block", padding: "3px 10px",
                        background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
                        borderRadius: 6, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: T.mono,
                      }}>{cfg.label}</span>
                    </div>
                  </div>
                  {/* Action buttons */}
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      className="tm-action-btn"
                      onClick={() => handleEdit(team)}
                      title="Edit Team"
                      style={{ background: "rgba(251,191,36,0.10)", border: "1px solid rgba(251,191,36,0.22)", color: T.amber }}
                      onMouseEnter={e => { e.currentTarget.style.background = "rgba(251,191,36,0.22)"; e.currentTarget.style.transform = "scale(1.08)"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "rgba(251,191,36,0.10)"; e.currentTarget.style.transform = "scale(1)"; }}
                    ><CiEdit size={18} /></button>
                    <button
                      className="tm-action-btn"
                      onClick={() => handleDelete(team.id, team.team_lead_name)}
                      title="Deactivate Team"
                      style={{ background: "rgba(248,113,113,0.10)", border: "1px solid rgba(248,113,113,0.22)", color: T.red }}
                      onMouseEnter={e => { e.currentTarget.style.background = "rgba(248,113,113,0.22)"; e.currentTarget.style.transform = "scale(1.08)"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "rgba(248,113,113,0.10)"; e.currentTarget.style.transform = "scale(1)"; }}
                    ><FaRegTrashAlt size={14} /></button>
                  </div>
                </div>

                {/* Mini-stats row */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
                  {[
                    { label: "Capacity", value: team.max_daily_capacity, unit: "sites", color: T.teal },
                    { label: "Upcoming", value: team.upcoming_assignments || 0, unit: "jobs", color: T.blue },
                  ].map(s => (
                    <div key={s.label} style={{ background: T.surf2, border: `1px solid ${T.border}`, borderRadius: 10, padding: "12px 14px" }}>
                      <div style={{ fontSize: 9, color: T.muted, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: 5 }}>{s.label}</div>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                        <span style={{ fontSize: 22, fontWeight: 800, color: s.color, fontFamily: T.mono, letterSpacing: "-0.5px" }}>{s.value}</span>
                        <span style={{ fontSize: 11, color: T.muted }}>{s.unit}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Team members */}
                {team.team_member_user_ids && team.team_member_user_ids.length > 0 && (
                  <div style={{ background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.18)", borderRadius: 10, padding: "10px 12px", marginBottom: 14 }}>
                    <div style={{ fontSize: 9, color: "#60A5FA", fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: 7 }}>
                      Team Members ({team.team_member_user_ids.length})
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                      {team.team_member_user_ids.map((uid, i) => (
                        <span key={i} style={{
                          padding: "3px 9px", background: "rgba(59,130,246,0.12)",
                          border: "1px solid rgba(59,130,246,0.25)", borderRadius: 6,
                          fontSize: 11, fontWeight: 600, color: "#93C5FD",
                        }}>{getUserName(uid)}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Footer info */}
                <div style={{ paddingTop: 14, borderTop: `1px solid ${T.border}`, display: "flex", flexDirection: "column", gap: 8 }}>
                  {/* Phone */}
                  {team.phone && (
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 30, height: 30, borderRadius: 8, background: "rgba(59,130,246,0.10)", border: "1px solid rgba(59,130,246,0.20)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <IoCall style={{ color: T.blue, fontSize: 13 }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 9, color: T.muted, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 1 }}>Phone</div>
                        <div style={{ fontSize: 13, color: T.text, fontWeight: 600 }}>{team.phone}</div>
                      </div>
                    </div>
                  )}

                  {/* History button */}
                  <div
                    onClick={() => handleViewHistory(team)}
                    title="Click to view job history"
                    style={{
                      display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
                      padding: "8px 10px", borderRadius: 9,
                      background: "rgba(52,211,153,0.05)", border: "1px solid rgba(52,211,153,0.18)",
                      transition: "all 0.15s",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = "rgba(52,211,153,0.12)"; e.currentTarget.style.borderColor = "rgba(52,211,153,0.32)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "rgba(52,211,153,0.05)"; e.currentTarget.style.borderColor = "rgba(52,211,153,0.18)"; }}
                  >
                    <div style={{ width: 30, height: 30, borderRadius: 8, background: "rgba(52,211,153,0.12)", border: "1px solid rgba(52,211,153,0.25)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <FaCheckCircle style={{ color: T.green, fontSize: 13 }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 9, color: T.muted, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 1, display: "flex", alignItems: "center", gap: 4 }}>
                        Total Completed <FaHistory style={{ fontSize: 9 }} />
                      </div>
                      <div style={{ fontSize: 13, color: T.green, fontWeight: 700, fontFamily: T.mono }}>
                        {team.total_assignments || 0} Jobs
                      </div>
                    </div>
                    <ChevronRight size={14} color={T.muted} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ── Empty state ── */
        <div style={{ background: T.surface, border: `2px dashed ${T.border}`, borderRadius: 18, padding: "70px 40px", textAlign: "center" }}>
          <div style={{ width: 100, height: 100, margin: "0 auto 20px", borderRadius: "50%", background: "rgba(59,130,246,0.08)", border: `1px solid rgba(59,130,246,0.18)`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Users size={44} color={T.blue} strokeWidth={1.5} />
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: T.text, marginBottom: 8 }}>
            {searchTerm || filterType !== "all" ? "No teams found" : "No teams yet"}
          </div>
          <div style={{ fontSize: 13, color: T.muted, marginBottom: 24, maxWidth: 360, margin: "0 auto 24px" }}>
            {searchTerm || filterType !== "all"
              ? "Try adjusting your search or filter criteria"
              : "Get started by creating your first installation team"}
          </div>
          {!searchTerm && filterType === "all" && (
            <button className="tm-btn-primary" onClick={openAddModal} style={{ margin: "0 auto" }}>
              <Plus size={15} /> Create First Team
            </button>
          )}
        </div>
      )}


      {/* ══════════════════════════════════════════
          ADD / EDIT TEAM MODAL
      ══════════════════════════════════════════ */}
      {showModal && (
        <Overlay onClick={() => { setShowModal(false); resetForm(); }}>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: T.surface, border: `1px solid ${T.bord2}`, borderRadius: 20,
              maxWidth: 640, width: "100%", maxHeight: "90vh", overflow: "hidden",
              boxShadow: "0 24px 80px rgba(0,0,0,0.65)", animation: "modalIn 0.2s ease both",
              display: "flex", flexDirection: "column",
            }}
          >
            {/* Modal header */}
            <div style={{ padding: "20px 24px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: "var(--nd-w02)" }}>
              <div>
                <div style={{ fontSize: 9, color: T.muted, letterSpacing: "0.18em", marginBottom: 4 }}>TEAM MANAGEMENT</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: "var(--nd-text-strong)", letterSpacing: "-0.02em" }}>
                  {editingTeam ? "Edit Team" : "Add New Team"}
                </div>
              </div>
              <CloseBtn onClick={() => { setShowModal(false); resetForm(); }} />
            </div>
            <div style={{ height: 2, background: "linear-gradient(90deg,#3B82F6,#22D3A5,transparent)", flexShrink: 0 }} />

            {/* Modal body */}
            <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
              <form onSubmit={handleSubmit} id="teamForm">

                {/* Team Lead */}
                <div style={{ marginBottom: 16 }}>
                  <label className="tm-label">Team Lead (User) <span style={{ color: T.red }}>*</span></label>
                  <select name="team_lead_user_id" value={formData.team_lead_user_id} onChange={handleInputChange} required className="tm-select">
                    <option value="">Select Team Lead</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.name} — {u.email} {u.role ? `(${u.role})` : ""}</option>
                    ))}
                  </select>
                </div>

                {/* Display name */}
                <div style={{ marginBottom: 16 }}>
                  <label className="tm-label">Team Display Name</label>
                  <input type="text" name="team_lead_name" value={formData.team_lead_name} onChange={handleInputChange} placeholder="Auto-filled from selected user" className="tm-input" />
                </div>

                {/* Team members */}
                <div style={{ marginBottom: 16 }}>
                  <label className="tm-label">Team Members <span style={{ color: T.muted, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(Optional)</span></label>
                  <select name="team_member_user_ids" multiple value={formData.team_member_user_ids} onChange={handleTeamMemberChange} className="tm-select-multi">
                    {users.filter(u => u.id !== parseInt(formData.team_lead_user_id)).map(u => (
                      <option key={u.id} value={u.id}>{u.name} — {u.email}</option>
                    ))}
                  </select>
                  <div style={{ fontSize: 11, color: T.muted, marginTop: 5 }}>Hold Ctrl / Cmd to select multiple members</div>
                </div>

                {/* Type + Capacity row */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
                  <div>
                    <label className="tm-label">Team Type</label>
                    <select name="team_type" value={formData.team_type} onChange={handleInputChange} className="tm-select">
                      <option value="commercial">🏢 Commercial</option>
                      <option value="industrial">🔄 Industrial</option>
                    </select>
                  </div>
                  <div>
                    <label className="tm-label">Max Daily Capacity</label>
                    <input type="number" name="max_daily_capacity" value={formData.max_daily_capacity} onChange={handleInputChange} min="1" max="10" className="tm-input" />
                  </div>
                </div>

                {/* Phone */}
                <div style={{ marginBottom: 16 }}>
                  <label className="tm-label">Phone Number</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="0300-1234567" className="tm-input" />
                </div>

                {/* Notes */}
                <div>
                  <label className="tm-label">Notes</label>
                  <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows={3} placeholder="Any additional information about this team…" className="tm-textarea" />
                </div>
              </form>
            </div>

            {/* Modal footer */}
            <div style={{ padding: "14px 24px", borderTop: `1px solid ${T.border}`, display: "flex", gap: 10, background: "var(--nd-w02)" }}>
              <button type="button" className="tm-btn-ghost" onClick={() => { setShowModal(false); resetForm(); }} style={{ flex: 1 }}>Cancel</button>
              <button type="submit" form="teamForm" className="tm-btn-primary" style={{ flex: 1 }}>
                {editingTeam ? "✓ Update Team" : "+ Create Team"}
              </button>
            </div>
          </div>
        </Overlay>
      )}


      {/* ══════════════════════════════════════════
          JOB HISTORY MODAL
      ══════════════════════════════════════════ */}
      {showHistoryModal && (
        <Overlay onClick={() => setShowHistoryModal(false)} zIndex={9999}>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: T.surface, border: `1px solid ${T.bord2}`, borderRadius: 20,
              maxWidth: 680, width: "100%", maxHeight: "85vh", overflow: "hidden",
              boxShadow: "0 24px 80px rgba(0,0,0,0.65)", animation: "modalIn 0.2s ease both",
              display: "flex", flexDirection: "column",
            }}
          >
            {/* Header */}
            <div style={{ padding: "20px 24px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: "var(--nd-w02)" }}>
              <div>
                <div style={{ fontSize: 9, color: T.muted, letterSpacing: "0.18em", marginBottom: 4 }}>JOB HISTORY</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "var(--nd-text-strong)", letterSpacing: "-0.02em", display: "flex", alignItems: "center", gap: 8 }}>
                  <FaHistory style={{ color: T.green, fontSize: 14 }} />
                  {selectedTeamHistory?.team_lead_name}'s Team
                </div>
              </div>
              <CloseBtn onClick={() => setShowHistoryModal(false)} />
            </div>
            <div style={{ height: 2, background: "linear-gradient(90deg,#22D3A5,#3B82F6,transparent)", flexShrink: 0 }} />

            {/* Body */}
            <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
              {historyLoading ? (
                <div style={{ textAlign: "center", padding: "50px 20px" }}>
                  <div style={{ width: 36, height: 36, border: `3px solid ${T.border}`, borderTop: `3px solid ${T.green}`, borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 12px" }} />
                  <div style={{ color: T.muted, fontSize: 13 }}>Loading history…</div>
                </div>
              ) : Array.isArray(jobHistoryData) && jobHistoryData.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {jobHistoryData.map((job, i) => (
                    <div key={job.id || i} className="tm-history-row">
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        {/* Index circle */}
                        <div style={{ width: 34, height: 34, borderRadius: "50%", background: "rgba(52,211,153,0.10)", border: "1px solid rgba(52,211,153,0.22)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: T.green, fontFamily: T.mono, flexShrink: 0 }}>
                          {i + 1}
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 3 }}>
                            {job.site_name || "Unknown Site"}
                          </div>
                          <div style={{ fontSize: 11, color: T.muted, fontFamily: T.mono }}>
                            {job.completed_at
                              ? new Date(job.completed_at).toLocaleDateString()
                              : job.scheduled_date
                                ? new Date(job.scheduled_date).toLocaleDateString()
                                : "N/A"}
                            {job.service_number && <span style={{ marginLeft: 8, color: T.faint }}>#{job.service_number}</span>}
                          </div>
                        </div>
                      </div>
                      <span style={{
                        padding: "3px 10px", background: "rgba(52,211,153,0.10)",
                        color: T.green, border: "1px solid rgba(52,211,153,0.25)",
                        borderRadius: 20, fontSize: 10, fontWeight: 700, fontFamily: T.mono,
                        textTransform: "uppercase", letterSpacing: "0.06em", flexShrink: 0,
                      }}>
                        {job.status ? job.status.replace("_", " ") : "Completed"}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "50px 20px" }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: T.muted }}>No completed jobs found for this team.</div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: "12px 24px", borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "flex-end", background: "var(--nd-w02)" }}>
              {!historyLoading && jobHistoryData.length > 0 && (
                <div style={{ fontSize: 11, color: T.muted, fontFamily: T.mono, marginRight: "auto", alignSelf: "center" }}>
                  <span style={{ color: T.green, fontWeight: 700 }}>{jobHistoryData.length}</span> completed job{jobHistoryData.length !== 1 ? "s" : ""}
                </div>
              )}
              <button className="tm-btn-ghost" onClick={() => setShowHistoryModal(false)}>Close</button>
            </div>
          </div>
        </Overlay>
      )}


      {/* ── Confirm Delete Dialog (unchanged) ── */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, teamId: null })}
        onConfirm={confirmDelete}
        title="Deactivate Team"
        message="Are you sure you want to deactivate this team?"
        confirmText="Deactivate"
        cancelText="Cancel"
        confirmColor="danger"
      />
    </div>
  );
};

export default TeamManagement;